import { initializeApp, getApps, FirebaseError } from "firebase/app";
import {
  browserLocalPersistence,
  getAuth,
  linkWithPopup,
  onAuthStateChanged,
  reauthenticateWithPopup,
  setPersistence,
  signInAnonymously,
  signInWithCredential,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  connectAuthEmulator,
  type User,
} from "firebase/auth";
import { initializeFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { isDeadSessionError } from "../account/authErrors";

/**
 * True when this page is being served from a machine that could plausibly be
 * running the emulators: localhost, a .local mDNS name, or a private LAN address
 * (the documented way to test from a phone on the same network).
 *
 * Anything else — web.app, firebaseapp.com, a custom domain — is a deployed
 * site, where "connect to localhost" can only ever mean the visitor's own
 * machine.
 */
function isLocalOrigin(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1" || host === "[::1]" || host === "::1") return true;
  if (host.endsWith(".local")) return true;
  // Private IPv4 ranges: 10/8, 192.168/16, 172.16/12.
  return /^10\.|^192\.168\.|^172\.(1[6-9]|2\d|3[01])\./.test(host);
}

// When running locally against the Firebase emulators (see scripts/dev), there are no real
// project credentials. The emulators accept any non-empty apiKey, so we fall back to harmless
// demo values and point the SDK at the local emulator ports instead of hitting Google.
//
// The origin check is a hard safety net, not a convenience. `import.meta.env` is
// baked in at BUILD time, and Vite layers process.env over .env files — so an
// exported VITE_USE_FIREBASE_EMULATORS (which scripts/dev.ps1 sets at session
// scope, and which therefore outlives the dev run) silently overrides the
// generated production config. That shipped a build that sent hosts to
// localhost:9099/emulator/auth/handler for Google sign-in. A deployed origin must
// never talk to an emulator, whatever the bundle was built with.
const emulatorsRequested = import.meta.env.VITE_USE_FIREBASE_EMULATORS === "true";
const useEmulators = emulatorsRequested && isLocalOrigin();

if (emulatorsRequested && !useEmulators) {
  console.error(
    "[fiveout] This build was compiled with VITE_USE_FIREBASE_EMULATORS=true but is being served " +
      `from ${window.location.origin}, so the emulator connection was refused and real Firebase is ` +
      "being used instead. Rebuild without that variable set — see docs/DEPLOYMENT.md.",
  );
}

// Populated from Vite env vars at build time (see .env.example / infra/terraform outputs).
// These are all public client config values, safe to ship in the bundle - Firestore/Cloud Run
// access is still governed by security rules + server-side auth checks, not by keeping this secret.
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || (useEmulators ? "demo-api-key" : undefined),
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || (useEmulators ? "localhost" : undefined),
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || (useEmulators ? "ga-fiveout-dev-01" : undefined),
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID || (useEmulators ? "demo-app-id" : undefined),
};

/**
 * Whether to make Firestore's real-time listener use long-polling rather than a
 * kept-open streaming response.
 *
 * By default the SDK holds one response open indefinitely and probes once, at
 * connection setup, for a proxy that buffers it. Anything in the path that
 * buffers — and Safari on iOS routinely has iCloud Private Relay in the
 * path — holds one update's bytes until the *next* update pushes them out. The
 * result is a client that is permanently about one move behind, including on
 * its own moves, and that a reload appears to fix because a new connection
 * re-probes and re-fetches. That is exactly the failure this game hit on an
 * iPhone while two desktop browsers on the same table stayed correct, and it is
 * cause (e) in FEATURE_REVIEW.md §3.4 — the only one of that review's five
 * desync causes never addressed.
 *
 * Forcing long-polling closes the response as soon as the backend sends data,
 * so there is nothing left for a proxy to sit on. It costs a connection setup
 * per update, which is why it is scoped to WebKit rather than applied to every
 * client: a turn-based card game writes a handful of documents a minute, but
 * the browsers that were never affected shouldn't pay for it either.
 *
 * Every browser on iOS is WebKit, whatever its badge says, so the check is for
 * the engine and not for Safari. `lp=1` / `lp=0` in the URL overrides it in
 * both directions, because the only machine that can answer "did this help?" is
 * the handset, and it needs to be able to try it both ways without a rebuild.
 * See docs/LOCAL_DEV.md.
 */
function shouldForceLongPolling(): boolean {
  if (typeof window === "undefined") return false;
  // HashRouter puts the app's own query string after the "#", so match the
  // whole URL rather than location.search (same reasoning as perfHud).
  const url = window.location.href;
  if (url.includes("lp=1")) return true;
  if (url.includes("lp=0")) return false;
  const ua = navigator.userAgent;
  const isWebKit = /AppleWebKit/.test(ua) && !/Chrome|Chromium|Edg\//.test(ua);
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.maxTouchPoints > 1 && /Macintosh/.test(ua));
  return isWebKit || isIOS;
}

export const forcedLongPolling = shouldForceLongPolling();

export const firebaseApp = getApps().length ? getApps()[0]! : initializeApp(firebaseConfig);
export const auth = getAuth(firebaseApp);
// initializeFirestore, not getFirestore: transport settings can only be supplied
// at first initialization, and this module is the only thing that creates it.
export const db = initializeFirestore(firebaseApp, {
  // Setting this clears the SDK's default experimentalAutoDetectLongPolling on
  // its own; passing both is an error.
  ...(forcedLongPolling ? { experimentalForceLongPolling: true } : {}),
});

if (useEmulators) {
  // Use whatever host the page came from rather than a hardcoded "localhost".
  // The design doc calls for testing across devices on the same network, and a
  // phone loading http://192.168.1.5:5173 would otherwise be told to reach the
  // emulators on its OWN localhost, where nothing is listening.
  const host = window.location.hostname;
  connectAuthEmulator(auth, `http://${host}:9099`, { disableWarnings: true });
  connectFirestoreEmulator(db, host, 8081);
}

// Local persistence is already the SDK's browser default; saying it out loud is
// the point. A session surviving a reload — and a closed tab — is a product
// promise now that accounts carry cloud saves (LIVE_SERVICES_PLAN §A2a), so it
// belongs in the code rather than in a default we happen to inherit.
//
// Set after the emulator wiring above, which has to be the first thing done to
// this auth instance. Fire-and-forget: this runs at module init, long before
// any sign-in the UI can trigger, and if storage is unavailable (Safari private
// browsing) the SDK falls back to in-memory auth for the session — a worse
// experience than a persisted session, but far better than refusing to load.
void setPersistence(auth, browserLocalPersistence).catch((err) => {
  console.warn("[fiveout] auth persistence could not be set to local; this session won't survive a reload", err);
});

let authReadyPromise: Promise<User> | null = null;

/**
 * Resolves once there is a signed-in user — restoring the stored session if
 * there is one, signing in anonymously if there isn't.
 *
 * Every online path has to await this first. Firebase restores auth state
 * ASYNCHRONOUSLY from IndexedDB, so `auth.currentUser` is null for the first
 * moments of every page load, and both things the online game depends on care:
 * the Firestore rules require `request.auth != null` to read a game, and the Go
 * server verifies an ID token on every move. Anything that fired immediately on
 * mount therefore ran unauthenticated on a cold load — the listener was denied
 * and the app sat on "Connecting…" forever, which is what a refresh, or an iOS
 * Safari tab being discarded and silently reloaded, would leave you with.
 *
 * Signing in anonymously when nothing was restored is deliberate: the identity
 * that actually seats a player travels in the URL (`?me=`), so an anonymous
 * token is enough to read the game and submit moves. A host who loses their
 * Google session drops to anonymous and can still play out the round; they'd
 * only need to sign in again to host a new game.
 */
export function authReady(): Promise<User> {
  if (authReadyPromise) return authReadyPromise;
  const pending = new Promise<User>((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        unsubscribe(); // one-shot: this is "is auth up yet", not a subscription
        if (user) resolve(user);
        else signInAnonymously(auth).then((cred) => resolve(cred.user), reject);
      },
      reject,
    );
  });
  // Don't cache a failure: a retry (see subscribeToGame) must be able to try
  // again rather than re-await the same rejected promise for the whole session.
  authReadyPromise = pending.catch((err) => {
    authReadyPromise = null;
    throw err;
  });
  return authReadyPromise;
}

/**
 * Signs in with Google, promoting the current anonymous session when there is one.
 *
 * Linking rather than a plain popup sign-in is what keeps a guest's progress:
 * everything they have played is attached to their anonymous uid, and
 * `linkWithPopup` keeps that uid rather than minting a new one
 * (LIVE_SERVICES_PLAN §A2a). Linking can only fail one interesting way — the
 * Google account already has a uid of its own — and then we sign in to that
 * account instead and let the first-sign-in merge prompt
 * (docs/CLOUD_SAVES_DESIGN.md §5) decide what happens to the local profile.
 * The abandoned anonymous uid never had anything server-side to lose.
 *
 * Google-only for now; a second provider is a second function here, not a
 * parameter, because each one's link/fallback dance differs.
 *
 * No button calls this directly: every sign-in goes through `useGatedSignIn`
 * (ui/components/SignInPolicy.tsx), which shows the privacy policy first when
 * it has not been acknowledged.
 */
export async function signInWithGoogle(): Promise<User> {
  const provider = new GoogleAuthProvider();
  const current = await liveAnonymousUser();
  if (current) {
    try {
      const linked = await linkWithPopup(current, provider);
      return linked.user;
    } catch (err) {
      if (isDeadSessionError(err)) {
        // The session died in the moments between the check above and this
        // call. Clear it and sign in cleanly rather than leaving the player on
        // a button that can only fail. The retry re-opens a popup, which a
        // blocker may refuse now that we are outside the original click — if it
        // does, the next press succeeds, because the corpse is gone by then.
        await discardSession();
        const retried = await signInWithPopup(auth, provider);
        return retried.user;
      }
      if (!(err instanceof FirebaseError) || err.code !== "auth/credential-already-in-use") throw err;
      // The popup already produced a usable credential; re-prompting for one
      // would make the player pick their account twice.
      const credential = GoogleAuthProvider.credentialFromError(err);
      if (!credential) throw err;
      const cred = await signInWithCredential(auth, credential);
      return cred.user;
    }
  }
  const cred = await signInWithPopup(auth, provider);
  return cred.user;
}

/**
 * The current anonymous guest, but only if the server still honours it —
 * otherwise null, with the dead session cleared on the way out.
 *
 * Linking is only meaningful onto a live account, and `auth.currentUser` is not
 * evidence of one: it is restored from IndexedDB and then believed until
 * something tries to use it. Locally, every `scripts/dev.sh` restart wipes the
 * Auth emulator's user store while the browser keeps its copy, so a guest
 * session from an earlier run is a routine state, not an exotic one. In
 * production the same shape arrives when an account is deleted, disabled, or
 * has its tokens revoked.
 *
 * Without this, `linkWithPopup` fails with `auth/invalid-refresh-token` **and
 * leaves the dead user in place**, so every subsequent press repeats it and
 * only clearing site data recovers. That is the bug this function exists for.
 *
 * The refresh is forced, because a cached ID token proves nothing about whether
 * the account behind it still exists — it is the refresh-token exchange that
 * asks the server, and that exchange is the whole point of the check. It costs
 * one round trip per press of a button nobody presses twice a minute.
 */
async function liveAnonymousUser(): Promise<User | null> {
  const current = auth.currentUser;
  if (!current?.isAnonymous) return null;
  try {
    await current.getIdToken(true);
    return current;
  } catch (err) {
    if (!isDeadSessionError(err)) throw err;
    await discardSession();
    return null;
  }
}

/** Drops a session the server has disowned, so the next attempt starts clean. */
async function discardSession(): Promise<void> {
  try {
    await signOut(auth);
  } catch {
    // Already gone as far as the SDK is concerned; the point was the clearing.
  }
  authReadyPromise = null;
}

/**
 * Signs out. The local profile deliberately stays on the device — logging out
 * is "stop syncing this account", not "wipe my game" — and the next online
 * action re-enters anonymous auth through `authReady()`.
 */
export async function signOutUser(): Promise<void> {
  await signOut(auth);
  // …which only holds if the cached promise goes with the session. authReady()
  // resolves once and keeps that User forever, so a stale one would hand the
  // next online action a token-less object for an account nobody is signed in
  // to (getIdToken() rejects), and the anonymous re-entry would never run.
  authReadyPromise = null;
}

/**
 * A fresh Google sign-in for the account already signed in, for the one route
 * that demands a recent one: deleting the account (the server refuses an
 * `auth_time` older than 5 minutes with `reauth`).
 *
 * Not `signInWithGoogle`: that links or switches accounts, and the point here is
 * to prove it is still the same person - a different Google account picked in
 * the popup fails with `auth/user-mismatch` rather than signing them in.
 *
 * The token is refreshed before returning so the retry cannot go out carrying
 * the old `auth_time`; that costs one round trip on a path walked once.
 */
export async function reauthenticateWithGoogle(): Promise<void> {
  const user = auth.currentUser;
  if (!user || user.isAnonymous) throw new Error("not signed in with Google");
  await reauthenticateWithPopup(user, new GoogleAuthProvider());
  await user.getIdToken(true);
}
